import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TargetIcon, Share2Icon, CameraIcon, CheckIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
export function ServicesPage() {
  const services = [{
    id: 'strategy',
    icon: <TargetIcon size={40} className="text-coral" data-id="element-297" />,
    title: 'Marketing Strategy',
    body: 'We develop clear, actionable marketing strategies tailored to your business. From audience targeting to content planning, every decision is intentional and aligned with growth.',
    includes: ['Brand positioning', 'Content strategy', 'Audience targeting', 'Campaign planning'],
    imageBg: 'bg-sage/40',
    image: '' // Add your image URL here, e.g. 'https://example.com/strategy.jpg'
  }, {
    id: 'social',
    icon: <Share2Icon size={40} className="text-coral" data-id="element-298" />,
    title: 'Social Media Management',
    body: 'Stay consistent, relevant, and visible. We manage your platforms with a focus on growth, engagement, and brand alignment.',
    includes: ['Content scheduling', 'Caption writing', 'Platform management', 'Performance tracking'],
    imageBg: 'bg-teal/10',
    image: '' // Add your image URL here, e.g. 'https://example.com/social.jpg'
  }, {
    id: 'content',
    icon: <CameraIcon size={40} className="text-coral" data-id="element-299" />,
    title: 'Content Creation',
    body: 'We create high-quality, on-brand content designed to stop the scroll and capture attention.',
    includes: ['Short-form video', 'Graphic design', 'Branded visuals', 'Promotional content'],
    imageBg: 'bg-coral/10',
    image: '' // Add your image URL here, e.g. 'https://example.com/content.jpg'
  }];
  return <div className="w-full pt-32 pb-20" data-id="element-300">
      {/* Header */}
      <section className="px-6 md:px-12 mb-24" data-id="element-301">
        <div className="max-w-4xl mx-auto text-center" data-id="element-302">
          <ScrollReveal data-id="element-303">
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-teal mb-8" data-id="element-304">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-teal/80 leading-relaxed" data-id="element-305">
              Our services are designed to support your brand at every
              stage—from strategy to execution.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services List */}
      <section className="px-6 md:px-12" data-id="element-306">
        <div className="max-w-7xl mx-auto space-y-32" data-id="element-307">
          {services.map((service, index) => <div key={service.id} className={`flex flex-col gap-12 lg:gap-24 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'}`} data-id="element-308">
              {/* Text Content */}
              <div className="flex-1 w-full" data-id="element-309">
                <ScrollReveal direction={index % 2 === 1 ? 'left' : 'right'} data-id="element-310">
                  <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm" data-id="element-311">
                    {service.icon}
                  </div>
                  <h2 className="text-4xl md:text-5xl font-heading font-bold text-teal mb-6" data-id="element-312">
                    {service.title}
                  </h2>
                  <p className="text-lg text-teal/80 mb-10 leading-relaxed" data-id="element-313">
                    {service.body}
                  </p>

                  <div className="bg-white/50 rounded-3xl p-8 border border-white" data-id="element-314">
                    <h3 className="font-heading font-bold text-xl text-teal mb-6" data-id="element-315">
                      What's Included:
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-316">
                      {service.includes.map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-317">
                          <div className="w-6 h-6 rounded-full bg-sage flex items-center justify-center shrink-0" data-id="element-318">
                            <CheckIcon size={14} className="text-teal" data-id="element-319" />
                          </div>
                          <span className="text-teal font-medium" data-id="element-320">{item}</span>
                        </li>)}
                    </ul>
                  </div>
                </ScrollReveal>
              </div>

              {/* Visual Placeholder */}
              <div className="flex-1 w-full" data-id="element-321">
                <ScrollReveal direction={index % 2 === 1 ? 'right' : 'left'} delay={0.2} data-id="element-322">
                  <div className={`aspect-square md:aspect-[4/3] rounded-[2.5rem] ${service.imageBg} relative overflow-hidden border border-white/40 shadow-lg`} data-id="element-323">
                    {service.image ? <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-324" /> : <div className="absolute inset-0 flex items-center justify-center opacity-30" data-id="element-325">
                        {service.icon}
                      </div>}
                  </div>
                </ScrollReveal>
              </div>
            </div>)}
        </div>
      </section>

      {/* CTA Section */}
      <section className="mt-32 px-6 md:px-12" data-id="element-326">
        <div className="max-w-5xl mx-auto bg-sage/30 rounded-[3rem] p-12 md:p-24 text-center border border-sage/50" data-id="element-327">
          <ScrollReveal data-id="element-328">
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-teal mb-6" data-id="element-329">
              Not sure what you need?
            </h2>
            <p className="text-xl text-teal/80 mb-10" data-id="element-330">
              Let's build a plan that works for you.
            </p>
            <Link to="/contact" data-id="element-331">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="bg-coral text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-coral-light transition-colors shadow-lg shadow-coral/20" data-id="element-332">
                Contact Us
              </motion.button>
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}